import { Form, FormState } from 'informed';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import feathersClient from '../../../apiClient';
import { useUser } from '../../../apiClient/ApiContext';
import { ErrorAlert, SuccessAlert } from '../../../components/alerts';
import { SubmitButton } from '../../../components/buttons';
import TyCheckbox from '../../../components/inputs/TyCheckbox';
import Card from '../../../components/Layout/Card/Card';
import Spinner from '@tymbe/ty-components/indicators/Spinner';
import { NotificationCategory, NotificationMethod } from '@tymbe/schema/enums';
import {
  NotificationPreferenceValue,
  PersonNotificationPreferencesData,
} from '@tymbe/schema/person-notification-preferences.interface';

type PersonNotificationForm = {
  [NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE]: boolean;
};

type PreferenceInput = Pick<PersonNotificationPreferencesData, 'category' | 'method' | 'person_id' | 'value'>;

function NotificationSettingsCard() {
  const { person } = useUser();
  const queryClient = useQueryClient();

  if (!person) {
    throw new Error('User not found');
  }

  const { data: a3NotificationPreference, isLoading } = useQuery(
    ['a3NotificationPreference', person.id],
    async () =>
      (
        (await feathersClient.service('person-notification-preferences').find({
          query: {
            person_id: person.id,
            method: NotificationMethod.MAIL,
            category: NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE,
          },
        })) as PersonNotificationPreferencesData[]
      )[0]
  );

  const { mutateAsync: setPreference } = useMutation(
    ['setA3NotificationPreference', person.id],
    (preference: PreferenceInput): Promise<PersonNotificationPreferencesData> =>
      feathersClient.service('person-notification-preferences').create(preference)
  );

  if (isLoading) {
    return <Spinner className="size-10 mx-auto my-5" />;
  }

  const onSubmit = async ({ values }: FormState<PersonNotificationForm>) => {
    const a3Preference: PreferenceInput = {
      person_id: person.id,
      method: NotificationMethod.MAIL,
      category: NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE,
      value: values[NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE]
        ? NotificationPreferenceValue.Notify
        : NotificationPreferenceValue.Skip,
    };

    try {
      await setPreference(a3Preference);
      SuccessAlert('Preference uloženy');
    } catch {
      ErrorAlert('Při ukládání nastavení došlo k chybě');
    }

    await queryClient.invalidateQueries(['a3NotificationPreference']);
  };

  const initialValues = {
    [NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE]:
      a3NotificationPreference?.value === NotificationPreferenceValue.Notify,
  };

  return (
    <Card style={{ marginTop: '10px' }}>
      <h3> Nastavení notifikací: </h3>
      <Form name="person-notification-form" onSubmit={onSubmit} initialValues={initialValues}>
        <div className="flex gap-6 flex-col">
          <TyCheckbox
            id={NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE}
            name={NotificationCategory.A3_LAST_MINUTE_APPLICATION_CHANGE}
            label="Upozornění na odhlášení brigádníka ze směny, která začíná za méně než 24 hodin"
          />
          <SubmitButton className="self-start ty-button-primary-color" buttontext="Uložit nastavení" />
        </div>
      </Form>
    </Card>
  );
}
export default NotificationSettingsCard;
